"use strict"

import React, { useEffect, useState } from "react"
import { FormControl, Card } from "react-bootstrap"

import { LocaleFormattedDate, LocaleFormattedUser } from "../../internationalization/LocaleFormatted"
import { isNumber } from "../../../shared_components/utils"
import RunMetadataCode from "../resources/RunMetadataCode"
import ChangeLogTable, { ChangeLogButton } from "../modals/ChangeLogTable"
import { RunParentDiv } from "./utils"
import { useSensePageWidth } from "../../samplemanagement/SampleScheduleTab/FormSamplePoint"
import { getFormattedMessage } from "../../shared_components_registration"

const chunkedArray = (arr, chunkSize = 2) =>
  [].concat // Chunking extra input fields into groups of 2
    .apply(
      [],
      arr.map(function (elem, i) {
        return i % chunkSize ? [] : [arr.slice(i, i + chunkSize)]
      }),
    )

export const ChunkedRunInfo = ({ arr, chunkSize = 2, intl, useDisabledFormFields = true }) => {
  const colClass = chunkSize === 1 ? "col-12" : chunkSize === 2 ? "col-6" : "col-4"
  return (
    <>
      {chunkedArray(arr, chunkSize).map((chunk, chunk_idx) => {
        return (
          <div className={"row"} key={`row${chunk_idx}`} style={{ marginBottom: "10px", fontSize: "90%" }}>
            {chunk.map((field, i) => (
              <div className={colClass} key={`row${chunk_idx}${i}`}>
                <div className={"row"}>
                  <div className={"col-4"}>
                    <label style={{ marginBottom: "0" }}>
                      <b>
                        {intl.formatMessage({
                          id: field.id,
                          description: `${field.description} Label`,
                          defaultMessage: field.description,
                        })}
                        :
                      </b>
                    </label>
                  </div>
                  <div className={"col-8"}>
                    {React.isValidElement(field.value) ? (
                      <span>{field.value}</span>
                    ) : useDisabledFormFields ? (
                      <FormControl
                        as={"textarea"}
                        disabled={true}
                        rows={
                          ["run_goal", "media_recipe", "gas_composition"].includes(field.description) ? 4 : 1
                        }
                        value={field.value}
                      />
                    ) : (
                      <span>{field.value}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </>
  )
}

export default function RunDetailsDiv({
  selectedRun = {},
  showParents = true,
  showLog = true,
  viewMetadata = true,
  viewExperiments = true,
  linkRunName = true,
  intl,
}) {
  const getTextStr = (id, msg, values) => getFormattedMessage(id, msg, true, intl, values)
  const { experiments = [] } = selectedRun
  const pageWidth = useSensePageWidth()
  const chunkSize = pageWidth < 995 ? 1 : 2
  const [showExperimentsSection, setShowExperimentsSection] = useState(true)
  useEffect(() => {
    viewExperiments !== showExperimentsSection && setShowExperimentsSection(viewExperiments)
  }, [])

  const labels = [
    {
      id: "graphing.run.placeholder",
      description: "Placeholder",
      value: <span className="fa fa-bookmark-o text-success" />,
      hide: !selectedRun.placeholder,
    },
    {
      id: "graphing.run.identifier",
      description: "Name",
      value: linkRunName ? (
        <a target={"_blank"} href={`/fermentation-management/run/${encodeURIComponent(selectedRun.name)}/`}>
          {selectedRun.name}
        </a>
      ) : (
        selectedRun.name
      ),
      hide: !("name" in selectedRun),
    },
    { id: "general.reactor", description: "Reactor", value: selectedRun.reactor },
    {
      id: "fermmanagement.started.on",
      description: "Started On",
      value: <LocaleFormattedDate date={selectedRun?.started_on} /> || "-",
      hide: !("started_on" in selectedRun),
    },
    {
      id: "fermmanagement.inoculated.on",
      description: "Inoculated On",
      value: <LocaleFormattedDate date={selectedRun?.inoculated_on} /> || "-",
      hide: !("inoculated_on" in selectedRun) || selectedRun.placeholder,
    },
    {
      id: "fermmanagement.inoculated.from",
      description: "Inoculated From",
      value: <RunParentDiv run={selectedRun} doHighlight={false} />,
      hide: selectedRun.placeholder || !showParents,
    },
    {
      id: "fermmanagement.started.by",
      description: "Started By",
      value: <LocaleFormattedUser user={selectedRun["started_by"] || {}} />,
      hide: !("started_by" in selectedRun),
    },
    {
      id: "general.description",
      description: "Description",
      value: selectedRun.run_description || "-",
    },
    {
      id: "general.comment",
      description: "Comment",
      value: selectedRun.run_comment || "-",
    },
    {
      id: "fermmanagement.earliest.data",
      description: "Earliest Data",
      value: isNumber(selectedRun.observation_min_day) ? (
        <span>
          <LocaleFormattedDate date={selectedRun.observation_min} />
          &nbsp;
          <em>
            {`(${intl.formatMessage({
              id: "graphing.timelabel.day",
            })}: ${selectedRun.observation_min_day.toFixed(3)})`}
          </em>
        </span>
      ) : (
        "-"
      ),
      hide: !("observation_min" in selectedRun),
    },
    {
      id: "fermmanagement.latest.data",
      description: "Latest Data",
      value: isNumber(selectedRun.observation_max_day) ? (
        <span>
          <LocaleFormattedDate date={selectedRun.observation_max} />
          &nbsp;
          <em>
            {`(${intl.formatMessage({
              id: "graphing.timelabel.day",
            })}: ${selectedRun.observation_max_day.toFixed(3)})`}
          </em>
        </span>
      ) : (
        "-"
      ),
      hide: !("observation_max" in selectedRun),
    },
    {
      id: "fermmanagement.run.length",
      description: "Run Length",
      value: isNumber(selectedRun["run_length"]) ? selectedRun["run_length"].toFixed(3) : "-",
      hide: !("run_length" in selectedRun),
    },
    {
      id: "fermmanagement.ended.by",
      description: "Ended By",
      value: <LocaleFormattedUser user={selectedRun["ended_by"] || {}} />,
      hide: !("ended_by" in selectedRun),
    },
    {
      id: "fermmanagement.finished.on",
      description: "Finished On",
      value: selectedRun.finished_on ? <LocaleFormattedDate date={selectedRun.finished_on} /> : "-",
      hide: !("finished_on" in selectedRun),
    },
  ].filter((f) => !f.hide)

  const [showChangeLog, setShowChangeLog] = useState(false)

  return (
    <>
      <div style={{ display: "flex" }}>
        {showLog && (selectedRun?.change_logs || []).length > 0 && (
          <ChangeLogButton
            intl={intl}
            onClick={() => setShowChangeLog(!showChangeLog)}
            isExpanded={showChangeLog}
            prefix={`${selectedRun.name}: `}
          />
        )}
      </div>
      {showLog && showChangeLog && <ChangeLogTable intl={intl} changeLogs={selectedRun?.change_logs || []} />}
      <div style={{ padding: "10px", marginBottom: "10px" }}>
        <ChunkedRunInfo intl={intl} arr={labels} chunkSize={chunkSize} useDisabledFormFields={false} />
      </div>
      {experiments.length > 0 && (
        <>
          <hr />
          <div style={{ padding: "10px" }}>
            <h5
              style={{ marginBottom: "20px", cursor: "pointer" }}
              onClick={() => setShowExperimentsSection(!showExperimentsSection)}
            >
              <span className={`fa fa-caret-${showExperimentsSection ? "down" : "right"}`} />
              &nbsp;
              <b>{getTextStr("general.project.experiments", "Experiments")}</b>
            </h5>
            {showExperimentsSection && (
              <>
                {chunkedArray(experiments, chunkSize === 1 ? 1 : 2).map((chunk, chunk_idx) => {
                  return (
                    <div
                      className={"row"}
                      key={`expRow${chunk_idx}`}
                      style={{ margin: "0", marginBottom: "10px" }}
                    >
                      {chunk.map((e, idx) => {
                        const { name, project_name, project_task_name, project_netsuite_project_id } = e
                        return (
                          <Card
                            key={`experiment${idx}`}
                            className={`mb-1 ${chunkSize === 1 ? "col-12" : "col-6"}`}
                            style={{ backgroundColor: "var(--lanzalake-green-lighter)" }}
                          >
                            <Card.Body style={{ padding: "1rem" }}>
                              <ChunkedRunInfo
                                intl={intl}
                                arr={[
                                  {
                                    id: "general.project.experiment",
                                    description: "Experiment",
                                    value: name,
                                  },
                                  {
                                    id: "fermmanagement.netsuite.project.id",
                                    description: "Netsuite Project ID",
                                    value: project_netsuite_project_id,
                                  },
                                  { id: "general.project", description: "Project", value: project_name },
                                  {
                                    id: "general.project.task",
                                    description: "Task",
                                    value: project_task_name,
                                  },
                                ]}
                                chunkSize={1}
                                useDisabledFormFields={false}
                              />
                            </Card.Body>
                          </Card>
                        )
                      })}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </>
      )}
      {Object.keys(selectedRun?.metadata || {}).length > 0 && (
        <RunMetadataCode
          metadata={selectedRun?.metadata}
          chunkSize={chunkSize}
          view={viewMetadata}
          intl={intl}
        />
      )}
    </>
  )
}
